'use client';

import { Button } from '@pickleballinc/react-ui';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import Switch from '@/components/Switch';
import { Session } from '@/lib/getSessionPages';
import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import {
  useCompleteGameDay,
  useEditLastRound,
  useLockAndUnlock,
  useRollBackToGameDay,
  useSaveRatingSequenceAndMove,
  useSaveSequenceRoundAndGenerateNextOne,
  useStartMatches,
  useStartOverGameDay,
  useStopMatches,
} from '@/modules/league/new-components/header/hooks';
import { GameDayInfo } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTotalPlayersInSession } from '@/modules/league/TotalPlayersInSession';
import { useUser } from '@pickleballinc/ui/components/global/UserInfoProvider';

interface GameDaySwitchesProps {
  params: { slug: string; sessionId: string; matchDateId: string };
  headerContent: GameDayInfo;
  user?: Session;
  currentSequenceNumber?: number;
  isAuthorized: boolean;
}
const GameDaySwitches = ({
  headerContent,
  params,
  currentSequenceNumber,
  isAuthorized,
}: GameDaySwitchesProps) => {
  const userData = useUser();
  const { details } = headerContent;

  const queryClient = useQueryClient();

  const [showLockPopup, setShowLockPopup] = useState(false);
  const [showLockPopupProblem, setShowLockPopupProblem] = useState(false);
  const [showStartMatchPopup, setShowStartMatchPopup] = useState(false);
  const [rollbackZeroScores, setRollbackZeroScores] = useState(false);
  const [showStartOverPopup, setShowStartOverPopup] = useState(false);
  const [showRollBackPopup, setShowRollBackPopup] = useState(false);
  const [showSaveAndStartNextRound, setShowSaveAndStartNextRound] = useState(false);
  const [showStartOverPopupDoubleCheck, setShowStartOverPopupDoubleCheck] = useState(false);
  const [showCompleteGameDayPopup, setShowCompleteGameDayPopup] = useState(false);
  const [showCompleteSequenceRound, setShowCompleteSequenceRound] = useState(false);

  const { matchDateId, slug } = params;

  const { mutate: saveLockAndUnlock } = useLockAndUnlock();
  const { mutate: startMatches } = useStartMatches();
  const { mutate: stopMatches } = useStopMatches();
  const { mutate: startOverGameDay } = useStartOverGameDay();
  const { mutate: rollBackToGameDay } = useRollBackToGameDay();
  const { mutate: completeGameDay } = useCompleteGameDay();
  const { mutate: saveRatingSequenceAndMove } = useSaveRatingSequenceAndMove();
  const { mutate: editLastRound } = useEditLastRound();
  const { mutate: saveSequenceRoundAndGenerateNextOne } = useSaveSequenceRoundAndGenerateNextOne();

  const { setLoading } = useLoader();
  const { totalPlayersInSession } = useTotalPlayersInSession();
  const searchParams = useSearchParams();

  useEffect(() => {
    setLoading(false);
  }, [headerContent, searchParams?.toString()]);

  const router = useRouter();
  const pathname = usePathname();
  const newSearchParams = new URLSearchParams(searchParams?.toString());
  const sequenceNumber = newSearchParams.get('sequenceNumber');
  const tab = newSearchParams.get('tab');

  useEffect(() => {
    if (tab === 'groups') {
      queryClient.invalidateQueries(['league_groups']);
    } else if (tab === 'matches') {
      queryClient.invalidateQueries(['league_matches']);
    }
  }, [sequenceNumber]);

  const buttonClasses =
    'rounded-md border 5rt3 bg-white !px-3 !py-2 !text-xs font-semibold lg:!text-sm tracking-wider !text-leagues-500 hover:bg-gray-100 border-gray-300 shadow-xs';

  const showEditLastRound =
    details.isRatingLeague &&
    details.isMatchStarted &&
    details.isLocked &&
    !details.DateLockScores &&
    currentSequenceNumber?.toString() === sequenceNumber &&
    parseInt(sequenceNumber || '1') > 1 &&
    details.TotalGamesAlreadyPlayed !== 0;

  const showChangeScores =
    !details.isFlexLeague &&
    details.DateLockScores &&
    details.isMatchStarted &&
    details.isLocked &&
    details.TotalGamesRemaining === 0 &&
    (details.TotalGamesRemaining === 0 || details.CanRollBackToThisMatchDate);

  const handleChangeScores = () => {
    const newSearchParams = new URLSearchParams(searchParams || '');
    newSearchParams.set('tab', 'change-scores');
    router.replace(`?${newSearchParams.toString()}`, { scroll: false });
  };

  const unlimitedNumberOfPlayersInSession = details.maxPlayersInSession === -1;
  const showPlayersInSessionButton =
    tab === 'rsvp' && !details.isLocked && !details.traditionalLeague && !details.isFlexLeague;
  const showAddPlayersInSessionButton =
    unlimitedNumberOfPlayersInSession || details.maxPlayersInSession > totalPlayersInSession;

  return (
    isAuthorized && (
      <div className='mx-auto flex h-auto w-full flex-row justify-between gap-2 px-3 py-1 md:flex-row md:px-3 md:py-0 md:pt-2'>
        {(!details.isMatchStarted || (details.isMatchStarted && !details.isLocked)) &&
        details.TotalTeamsYes > 0 &&
        ((details.scramble && details.TotalTeamsYes >= 4) ||
          (details.partnerOrSingles && details.TotalTeamsYes >= 2)) ? (
          <div
            className='flex items-center'
            onClick={() => {
              if (
                (details.scramble && details.TotalTeamsYes < 4) ||
                (details.partnerOrSingles && details.TotalTeamsYes < 2)
              ) {
                setShowLockPopupProblem(true);
              } else {
                setShowLockPopup(true);
              }
            }}
          >
            <Switch
              defaultChecked={!!details.isLocked}
              borderColor={'border-leagues-300/50'}
              bgColor={'bg-leagues-300/50'}
            />
            <label className='ml-2 text-sm hover:cursor-pointer lg:text-base'>
              Lock Opt In/Out
            </label>
          </div>
        ) : null}
        {showPlayersInSessionButton && showAddPlayersInSessionButton ? (
          <CustomLink href={`${pathname}?add=true`}>
            <Button
              variant='link-gray'
              type='button'
              size='xs'
              className={`${buttonClasses} !border-leagues-700 !bg-leagues-700 !text-white hover:!bg-leagues-500`}
            >
              <p className='text-xs font-semibold'>Add players</p>
            </Button>
          </CustomLink>
        ) : showPlayersInSessionButton ? (
          <Button
            variant='link-gray'
            type='button'
            size='xs'
            className={`${buttonClasses} !border-leagues-700 !bg-leagues-700 !text-white !cursor-text`}
          >
            <p className='text-xs font-semibold'>Players Full</p>
          </Button>
        ) : null}
        {details.isLocked &&
        details.TotalGamesAlreadyPlayed === 0 &&
        details.TotalGamesRemaining === details.MatchCount ? (
          <div
            className='flex items-center'
            onClick={() => {
              setShowStartMatchPopup(true);
            }}
          >
            <Switch
              defaultChecked={!!details.isMatchStarted}
              borderColor={'border-leagues-300/50'}
              bgColor={'bg-leagues-300/50'}
            />
            <label className='ml-2 text-sm hover:cursor-pointer lg:text-base'>Start Matches</label>
          </div>
        ) : null}
        {(!details.isFlexLeague &&
          details.DateLockScores &&
          details.isMatchStarted &&
          details.isLocked &&
          details.TotalGamesRemaining === 0 &&
          (details.TotalGamesRemaining === 0 || details.CanRollBackToThisMatchDate) &&
          details?.NumberOfFutureMatchDatesStarted === 0) ||
        (details?.isMatchStarted &&
          details?.isLocked &&
          details?.NumberOfFutureMatchDatesStarted === 0 &&
          details?.MatchCount &&
          details.TotalGamesRemaining < details?.MatchCount &&
          details?.CanRollBackToThisMatchDate === false) ? (
          <div className='flex flex-col items-start justify-end gap-2 md:flex-row md:items-end md:gap-4'>
            {!details.isFlexLeague &&
              details.DateLockScores &&
              details.isMatchStarted &&
              details.isLocked &&
              details.TotalGamesRemaining === 0 &&
              (details.TotalGamesRemaining === 0 || details.CanRollBackToThisMatchDate) &&
              details?.NumberOfFutureMatchDatesStarted === 0 && (
                <Button
                  variant='link-gray'
                  className={`${buttonClasses}`}
                  onClick={() => {
                    setShowRollBackPopup(true);
                  }}
                >
                  Roll back
                </Button>
              )}
            {showEditLastRound && (
              <Button
                variant='link-gray'
                className={`${buttonClasses} hidden !border-gray-900 !bg-gray-900 !text-xs !text-white hover:!bg-gray-800 md:flex lg:!text-sm`}
                onClick={() => {
                  setLoading(true);
                  editLastRound(
                    { matchDateId, sequenceNumber: parseInt(sequenceNumber || '1') },
                    {
                      onSuccess: (data) => {
                        if (data.message) {
                          toast.success(data.message);
                        }
                        if (sequenceNumber) {
                          newSearchParams.set('sequenceNumber', `${parseInt(sequenceNumber) - 1}`);
                          router.replace(`?${newSearchParams.toString()}`, { scroll: false });
                        }
                        queryClient.invalidateQueries(['league_game_day_header']);
                      },
                      onError: () => {
                        toast.error('Failed to save changes to the previous round.');
                        setLoading(false);
                      },
                    }
                  );
                }}
              >
                Edit Last Round
              </Button>
            )}
            {details?.isMatchStarted &&
              details?.isLocked &&
              details?.NumberOfFutureMatchDatesStarted === 0 &&
              details?.MatchCount &&
              details.TotalGamesRemaining < details?.MatchCount &&
              details?.CanRollBackToThisMatchDate === false &&
              !details.DateLockScores && (
                <Button
                  variant='link-gray'
                  className={`${buttonClasses} hidden md:flex`}
                  onClick={() => {
                    setShowStartOverPopup(true);
                  }}
                >
                  Start over
                </Button>
              )}
          </div>
        ) : null}

        {(details.isRatingLeague && details.isMatchStarted && details.isLocked) ||
        (details?.isMatchStarted &&
          details?.isLocked &&
          details?.NumberOfFutureMatchDatesStarted === 0 &&
          details?.TotalGamesRemaining === 0 &&
          details?.CanRollBackToThisMatchDate === false &&
          details?.MatchCount) ||
        (details.isRatingLeague && details.isMatchStarted) ? (
          <div className='flex flex-col items-end justify-end gap-2 md:flex-row md:justify-normal md:gap-4'>
            {details.isRatingLeague &&
              details.isMatchStarted &&
              details.isLocked &&
              !details.DateLockScores &&
              currentSequenceNumber?.toString() === sequenceNumber &&
              details.TotalGamesAlreadyPlayed !== 0 && (
                <Button
                  variant='link-gray'
                  className={`${buttonClasses} hidden !border-gray-900 !bg-gray-900 !text-xs !text-white hover:!bg-gray-800 md:flex lg:!text-sm`}
                  onClick={() => {
                    setShowSaveAndStartNextRound(true);
                  }}
                >
                  Save & Start Next Round
                </Button>
              )}
            {details?.isMatchStarted &&
            details?.isLocked &&
            details?.NumberOfFutureMatchDatesStarted === 0 &&
            details?.TotalGamesRemaining === 0 &&
            details?.CanRollBackToThisMatchDate === false &&
            details?.MatchCount &&
            !details.DateLockScores &&
            details.rankingType === 'UpDown' &&
            details.numberOfSequences > 1 &&
            !details.DidGenerate2ndSequence ? (
              <Button
                variant='link-gray'
                className={`${buttonClasses} !border-leagues-700 !bg-leagues-700 !text-white hover:!bg-leagues-500`}
                onClick={() => {
                  setShowCompleteSequenceRound(true);
                }}
              >
                Complete Sequence 1
              </Button>
            ) : null}
            {(details?.isMatchStarted &&
              details?.isLocked &&
              details?.NumberOfFutureMatchDatesStarted === 0 &&
              details?.TotalGamesRemaining === 0 &&
              details?.CanRollBackToThisMatchDate === false &&
              details?.MatchCount &&
              !details.DateLockScores &&
              details.rankingType !== 'UpDown') ||
            (details.isRatingLeague &&
              details.isMatchStarted &&
              details.TotalGamesAlreadyPlayed !== 0 &&
              !details.DateLockScores) ||
            (details.rankingType === 'UpDown' &&
              (details.DidGenerate2ndSequence || details.numberOfSequences === 1) &&
              details?.isLocked &&
              details?.NumberOfFutureMatchDatesStarted === 0 &&
              details?.TotalGamesRemaining === 0 &&
              details?.MatchCount &&
              !details.DateLockScores) ? (
              <Button
                variant='link-gray'
                className={`${buttonClasses} !border-leagues-700 !bg-leagues-700 !text-white hover:!bg-leagues-500`}
                onClick={() => {
                  setShowCompleteGameDayPopup(true);
                }}
              >
                {details.isRatingLeague ? 'Complete Ratings Session' : 'Complete game day'}
              </Button>
            ) : null}
          </div>
        ) : null}
        {showChangeScores && (
          <Button
            variant='link-gray'
            className={`${buttonClasses} !border-leagues-700 !bg-leagues-700 !text-white hover:!bg-leagues-500`}
            onClick={handleChangeScores}
          >
            Change Scores
          </Button>
        )}

        <ConfirmationModal
          open={showRollBackPopup}
          onClose={() => setShowRollBackPopup(false)}
          onConfirm={() => {
            setLoading(true);
            rollBackToGameDay(
              { matchDateId, rollbackZeroScores },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  queryClient.invalidateQueries(['league_game_day_header']);
                },
                onError: () => {
                  toast.error('Failed to roll back to game day.');
                  setLoading(false);
                },
              }
            );
            setShowRollBackPopup(false);
          }}
          title='Rollback Game Day'
          rollbackZeroScores={rollbackZeroScores}
          setRollbackZeroScores={setRollbackZeroScores}
        />
        <ConfirmationModal
          open={showStartOverPopupDoubleCheck}
          onClose={() => setShowStartOverPopupDoubleCheck(false)}
          onConfirm={() => {
            setLoading(true);
            startOverGameDay(
              { matchDateId },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  queryClient.invalidateQueries(['league_game_day_header']);
                },
                onError: () => {
                  toast.error('Failed to start over the game day.');
                  setLoading(false);
                },
              }
            );
            setShowStartOverPopupDoubleCheck(false);
          }}
          title='Have to ask again. Are you sure?'
          message='Please NOTE: THIS ACTION CANNOT BE UNDONE!'
          confirmButtonText='Yes'
        />
        <ConfirmationModal
          open={showStartOverPopup}
          onClose={() => setShowStartOverPopup(false)}
          onConfirm={() => {
            setShowStartOverPopupDoubleCheck(true);
            setShowStartOverPopup(false);
          }}
          title='Zero Scores and Start Over?'
          message='This will put the status back to NOT Started.'
          confirmButtonText='Continue'
        />
        <ConfirmationModal
          open={showCompleteGameDayPopup}
          onClose={() => setShowCompleteGameDayPopup(false)}
          onConfirm={() => {
            setLoading(true);
            if (details.rankingType === 'UpDown') {
              const nextSequenceNumber =
                details.numberOfSequences > 1 && details.DidGenerate2ndSequence ? '2' : '1';
              saveSequenceRoundAndGenerateNextOne(
                { matchDateId: details.MatchDateID, sequenceNumber: parseInt(nextSequenceNumber) },
                {
                  onSuccess: (data) => {
                    if (data.message) {
                      toast.success(data.message);
                    }
                    queryClient.invalidateQueries(['league_game_day_header']);
                  },
                  onError: () => {
                    toast.error('Failed to complete the round.');
                    setLoading(false);
                  },
                }
              );
            } else {
              completeGameDay(
                { matchDateId },
                {
                  onSuccess: (data) => {
                    if (data.message) {
                      toast.success(data.message);
                    }
                    queryClient.invalidateQueries(['league_game_day_header']);
                  },
                  onError: () => {
                    toast.error('Something went wrong and we were unable to complete game day.');
                    setLoading(false);
                  },
                }
              );
            }
            setShowCompleteGameDayPopup(false);
          }}
          title='Complete Game Day?'
          message='This will lock all scores and calculate all stats. It will save all completed games and mark all incomplete games as canceled.'
          showEachSentenceInNewLine
        />
        <ConfirmationModal
          open={showCompleteSequenceRound}
          onClose={() => setShowCompleteSequenceRound(false)}
          onConfirm={() => {
            setLoading(true);

            saveSequenceRoundAndGenerateNextOne(
              { matchDateId: details.MatchDateID, sequenceNumber: 1 },
              {
                onSuccess: () => {
                  if (sequenceNumber === '1') {
                    newSearchParams.delete('matchGroup');
                    newSearchParams.set('sequenceNumber', '2');
                    router.push(
                      `/leagues/${slug}/session/${details.sessionId}/matchdate/${details.MatchDateID}?tab=matches&sequenceNumber=2`
                    );
                  }
                  queryClient.invalidateQueries(['league_game_day_header']);
                },
                onError: () => {
                  toast.error('Failed to complete sequence round');
                  setLoading(false);
                },
              }
            );

            setShowCompleteSequenceRound(false);
          }}
          title='Complete Round 1?'
          message='This will lock Round 1 and generate Round 2. This action CANNOT be undone.'
          confirmButtonText='Continue'
          confirmButtonVariant='primary'
        />
        <ConfirmationModal
          open={showStartMatchPopup}
          onClose={() => setShowStartMatchPopup(false)}
          onConfirm={async () => {
            setLoading(true);
            if (!details?.isMatchStarted) {
              startMatches(
                { matchDateId },
                {
                  onSuccess: (data) => {
                    if (data.message) {
                      toast.success(data.message);
                    }
                    queryClient.invalidateQueries(['league_game_day_header']);
                  },
                  onError: () => {
                    toast.error('Failed to start matches.');
                    setLoading(false);
                  },
                }
              );
            } else {
              stopMatches(
                { matchDateId },
                {
                  onSuccess: (data) => {
                    if (data.message) {
                      toast.success(data.message);
                    }
                    queryClient.invalidateQueries(['league_game_day_header']);
                  },
                  onError: () => {
                    toast.error('Failed to stop matches.');
                    setLoading(false);
                  },
                }
              );
            }
            setShowStartMatchPopup(false);
          }}
          title={!details?.isMatchStarted ? 'Start Matches?' : 'Stop Matches?'}
          message={
            !details?.isMatchStarted
              ? 'This will start matches and allow score entry.'
              : 'All matches will be removed.'
          }
          confirmButtonVariant='primary'
        />
        <ConfirmationModal
          open={showLockPopup}
          onClose={() => setShowLockPopup(false)}
          onConfirm={async () => {
            if (
              (details.scramble && details.TotalTeamsYes >= 4) ||
              (details.partnerOrSingles && details.TotalTeamsYes >= 2)
            ) {
              setLoading(true);
              saveLockAndUnlock(
                { matchDateId, isLocked: details?.isLocked },
                {
                  onSuccess: (data) => {
                    if (data.message) {
                      toast.success(data.message);
                    }
                    queryClient.invalidateQueries(['league_game_day_header']);
                    queryClient.invalidateQueries(['league_groups']);
                  },
                  onError: () => {
                    toast.error('Failed to lock/unlock opt in.');
                    setLoading(false);
                  },
                }
              );
            } else {
              setShowLockPopupProblem(true);
            }
            setShowLockPopup(false);
          }}
          title={details?.isLocked ? 'Unlock Opt In/Out?' : 'Lock Opt In/Out?'}
          message={
            details?.isLocked
              ? 'This will remove all created group splits. This will also ALLOW players to opt in and opt out if time remains.'
              : 'This will LOCK player opt in and opt out.'
          }
          confirmButtonVariant='primary'
        />
        <ConfirmationModal
          open={showLockPopupProblem}
          onClose={() => setShowLockPopupProblem(false)}
          onConfirm={() => setShowLockPopupProblem(false)}
          title={
            (details.scramble && details.TotalTeamsYes < 4) ||
            (details.partnerOrSingles && details.TotalTeamsYes < 2)
              ? 'Not able to Lock Opt In/Out'
              : 'Error'
          }
          message={
            (details.scramble && details.TotalTeamsYes < 4) ||
            (details.partnerOrSingles && details.TotalTeamsYes < 2)
              ? `For  ${details.scramble ? 'scramble' : 'partner or singles '} league you need to have a minimum of ${
                  details.scramble ? '4 players' : '2 players or groups'
                } opted in.`
              : 'Error'
          }
          confirmButtonVariant='primary'
        />
        <ConfirmationModal
          open={showSaveAndStartNextRound}
          onClose={() => setShowSaveAndStartNextRound(false)}
          onConfirm={async () => {
            setLoading(true);
            const fullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`;
            saveRatingSequenceAndMove(
              {
                matchDateId: details.MatchDateID,
                sequenceNumber: parseInt(sequenceNumber || '1'),
                fullName,
              },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  if (sequenceNumber) {
                    newSearchParams.set('sequenceNumber', `${parseInt(sequenceNumber) + 1}`);
                    router.replace(`?${newSearchParams.toString()}`, { scroll: false });
                  }
                },
                onError: () => {
                  toast.error('Failed to save current and start next round.');
                  setLoading(false);
                },
              }
            );
            setShowSaveAndStartNextRound(false);
          }}
          title={'Save and Start Next Round?'}
          message={'This will save all scores and start next round.'}
          confirmButtonVariant='primary'
        />
      </div>
    )
  );
};

export default GameDaySwitches;
